import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, map } from 'rxjs';
import { AttendanceFeeSummary, DateRanges, RedirectRequest, TotalInfo, YearsSelected } from '../../models/statistics.model';
import { ChartConfiguration, DataChart, RedirectParam, SummaryFee, SummaryMetric } from '../../models/data-chart-models';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../../environments/environment';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { BillingTypes, CollectionTypes, LiquidationTypes } from '../../models/filters.models';
import moment from 'moment';
import { EnvService } from '../../../shared/services/env.service';

@Injectable({
  providedIn: 'root'
})
export class StatisticsService {
  private annualSummaryData = new BehaviorSubject<any>(null);
  private annualFeeData = new BehaviorSubject<any>(null);
  private annualFeePercentageData = new BehaviorSubject<any>(null);
  private annualFeeYears: string[] = [];
  private annualSummaryYears: string[] = [];

  annualSummaryData$ = this.annualSummaryData.asObservable();
  annualFeeData$ = this.annualFeeData.asObservable();
  annualFeePercentageData$ = this.annualFeePercentageData.asObservable();

  configurationStyles = this.envService.getConfigurationStyles();

  constructor(private http: HttpClient, private translateService: TranslateService, private envService: EnvService) { }

  getStatistics(yearsSelected?: YearsSelected): void {
    this.getAnnualSummaryStatistics(yearsSelected?.annualSummaryYears);
    this.getAnnualFeeStatistics(yearsSelected?.annualFeeYears);
  }

  getAnnualSummaryStatistics(yearsSelected?: string[]) {
    let params = new HttpParams();

    let fromDate, toDate;

    if (yearsSelected && yearsSelected.length) {
      const sorteredYears = yearsSelected.sort((a, b) => a < b ? 1 : -1);
      const higherYear = new Date(`${sorteredYears[0]}`).getUTCFullYear();
      const lowerYear = new Date(`${sorteredYears[1]}`).getUTCFullYear();
      fromDate = moment([lowerYear, 11, 31]).format('YYYY-MM-DD');
      toDate =  moment([higherYear, 11, 31]).format('YYYY-MM-DD');

      this.annualSummaryYears = sorteredYears;
    } else {
      const currentYear = new Date().getUTCFullYear();
      const lastYear = currentYear - 1;
      fromDate = moment([lastYear, 0, 1]).format('YYYY-MM-DD');
      toDate = moment([currentYear, 11, 31]).format('YYYY-MM-DD');
    }

    params = params.append('additionalInfo.auditInfo', false);
    params = params.append('additionalInfo.resultList', false);
    params = params.append('additionalInfo.summaryInfo', false);
    params = params.append('additionalInfo.totalInfo', false);

    params = params.append('additionalInfo.distinctInfo', true);
    params = params.append('additionalInfo.dateRangeType', 'A'); // Range date Annual

    params = params.append('paginate.limit', 0);
    params = params.append('paginate.offset', 0);

    params = params.append('criteria.attendanceFromDate', fromDate);
    params = params.append('criteria.attendanceToDate', toDate);

    this.http.get(`${environment.URL_API}remunerations/search`, { params: params }).pipe(
      map((response: any) => {
        let { distinctInfo } = response;

        const annualSummaryData = this.prepareAnnualSummaryData(distinctInfo.dateRanges);

        return { annualSummaryData, yearsSelected }
      }),

    ).subscribe(response => {
      this.annualSummaryData.next(response.annualSummaryData);
    })
  }

  getAnnualFeeStatistics(yearsSelected?: string[]) {
    let params = new HttpParams();

    let fromDate, toDate;
    let yearSelected: string;

    if (yearsSelected && yearsSelected.length) {
      yearSelected = yearsSelected[0];
      const higherYear = new Date(`${yearsSelected[0]}`).getUTCFullYear();
      const lowerYear = new Date(`${yearsSelected[0]}`).getUTCFullYear();
      fromDate = moment([lowerYear, 0, 1]).format('YYYY-MM-DD');
      toDate = moment([higherYear, 11, 31]).format('YYYY-MM-DD');

      this.annualFeeYears = yearsSelected;
    } else {
      const currentYear = new Date().getUTCFullYear();
      yearSelected = currentYear.toString();
      fromDate = moment([currentYear, 0, 1]).format('YYYY-MM-DD');
      toDate = moment([currentYear, 11, 31]).format('YYYY-MM-DD');
    }

    params = params.append('additionalInfo.auditInfo', false);
    params = params.append('additionalInfo.resultList', false);
    params = params.append('additionalInfo.summaryInfo', false);
    params = params.append('additionalInfo.distinctInfo', false);

    params = params.append('additionalInfo.totalInfo', true);
    params = params.append('additionalInfo.dateRangeType', 'A'); // Range date Annual

    params = params.append('paginate.limit', 0);
    params = params.append('paginate.offset', 0);

    params = params.append('criteria.attendanceFromDate', fromDate);
    params = params.append('criteria.attendanceToDate', toDate);


    this.http.get(`${environment.URL_API}remunerations/search`, { params: params }).pipe(
      map((response: any) => {
        let { totalInfo } = response;
        const annualFeeData = this.prepareAnnualFeeData(totalInfo, yearSelected);
        const annualFeePercentageData = this.prepareAnnualFeePercentageData(totalInfo.totalBillingAmount,
          totalInfo.totalBillingPercentage, yearSelected);

        return { annualFeeData, annualFeePercentageData };
      }),

    ).subscribe(response => {
      this.annualFeeData.next(response.annualFeeData);
      this.annualFeePercentageData.next(response.annualFeePercentageData);
    })
  }

  getLastRedirectSearch() {
    const lastRedirectSearch = sessionStorage.getItem('lastRedirectSearch');
    if (lastRedirectSearch) {
      return JSON.parse(lastRedirectSearch);
    } else {
      return '';
    }
  }

  getAnnualFeeYears() {
    return this.annualFeeYears;
  }

  getAnnualSummaryYears() {
    return this.annualSummaryYears;
  }

  //In case of changing the attributes of the 'RedirectRequest' interface,
  //remember to add this change in the formControl of 'fee.component'
  mapRedirectSearch(redirectParam: RedirectParam) {
    let lastRedirectSearch: RedirectRequest = {};
    const year = new Date(`${redirectParam.value}`).getUTCFullYear();

    const fromDate = moment([year, 0, 1]).format('YYYY-MM-DD');
    const toDate = moment([year, 11, 31]).format('YYYY-MM-DD');

    lastRedirectSearch.attendanceFromDate = fromDate;
    lastRedirectSearch.attendanceToDate =  toDate;

    switch (redirectParam.paramId) {
      case 'billingStatus': {
        const billingIds = Object.values(BillingTypes);
        lastRedirectSearch.billingStatusIds = billingIds;

        break;
      }

      case `liquidationStatus_${LiquidationTypes.LIQUIDATED}`: {
        lastRedirectSearch.liquidationStatusIds = [LiquidationTypes.LIQUIDATED];
        break;
      }

      case `liquidationStatus_${LiquidationTypes.NOT_LIQUIDATED}`: {
        lastRedirectSearch.liquidationStatusIds = [LiquidationTypes.NOT_LIQUIDATED];

        break;
      }

      case `collectionStatus`: {
        lastRedirectSearch.liquidationStatusIds = [LiquidationTypes.NOT_LIQUIDATED];

        break;
      }

      case `collectionStatus_${CollectionTypes.CLOSED}`: {
        lastRedirectSearch.collectionStatusIds = [CollectionTypes.CLOSED];
        lastRedirectSearch.liquidationStatusIds = [LiquidationTypes.NOT_LIQUIDATED];

        break;
      }

      case `collectionStatus_${CollectionTypes.NOT_CLOSED}`: {
        lastRedirectSearch.collectionStatusIds = [CollectionTypes.NOT_CLOSED];
        lastRedirectSearch.liquidationStatusIds = [LiquidationTypes.NOT_LIQUIDATED];
        break;
      }

    }

    sessionStorage.setItem('lastRedirectSearch', JSON.stringify(lastRedirectSearch));
  }

  private prepareAnnualSummaryData(dateRanges: DateRanges[]): DataChart[] {
    let dataCharts: DataChart[] = [];

    const chartTitles = {
      qttPatient: this.translateService.instant('home-page.summary.patient-label'),
      qttAttendanceNumber: this.translateService.instant('home-page.summary.attendance-number-label'),
      qttAttendanceDescription: this.translateService.instant('home-page.summary.attendance-description-label'),
      qttBillingNumber: this.translateService.instant('home-page.summary.billing-number-label'),
      qttLiquidationNumber: this.translateService.instant('home-page.summary.liquidation-number-label'),
    };

    const chartMap: Record<string, DataChart> = {};

    //The 'chartMap' is initialized with the keys defined in 'chartTitles'.
    Object.keys(chartTitles).forEach(key => {
      chartMap[key] = {
        chartConfigurations: [],
        title: chartTitles[key as keyof typeof chartTitles]
      };
    });

    //Only the selected years are returned, which will always be the first and last year.
    const dateRangesSelected = [dateRanges[dateRanges.length - 1], dateRanges[0]];

    //Each previously initialized value of the ChartMap is found by its 'key'(which must be the same as
    //the one returned by the service) and the values are set in the chartConfigurations
    dateRangesSelected.forEach((dateRange: DateRanges) => {
      const { qttCompany, qttCenter, fromDate, toDate, ...dateRangeDTO } = dateRange;
      Object.keys(dateRangeDTO).forEach(key => {
        if (chartMap[key]) {
          chartMap[key].chartConfigurations.push({
            label: new Date(`${fromDate}`).getUTCFullYear().toLocaleString(),
            value: dateRangeDTO[key as keyof typeof dateRangeDTO],
            backgroundColor: chartMap[key].chartConfigurations[0] ? '#E7E7EA' : this.configurationStyles.PCOL
          });
        }
      });
    });

    //Each chartMap is traversed and the values are put into the dataCharts array
    Object.keys(chartMap).forEach(key => {
      dataCharts.push(chartMap[key]);
    });

    return dataCharts;
  }

  private prepareAnnualFeeData(totalInfo: TotalInfo, yearSelected: string): SummaryFee[] {
    const { byCollectionStatus, byLiquidationStatus, totalAttendanceAmount } = totalInfo.attendanceAmount;

    let dataFee: SummaryFee[] = [];

    const totalTitleLiquidated = totalAttendanceAmount ?? 0;
    const totalTitleNotLiquidated = byLiquidationStatus.find(liquidation =>
      liquidation.liquidationStatusId === LiquidationTypes.NOT_LIQUIDATED)?.attendanceAmount ?? 0;

    const feeInformation = {
      byLiquidationStatus: {
        totalTitle: totalTitleLiquidated,
        title: `${this.translateService.instant('home-page.summary.total-fees')} ${yearSelected}`,
        redirectParam: {
          paramId: 'liquidationStatus',
          value: yearSelected
        },
        icon: {
          name: 'stethoscope' as IconName,
          color: this.configurationStyles.PCOL!
        }

      },
      byCollectionStatus: {
        totalTitle: totalTitleNotLiquidated,
        title: `${this.translateService.instant('home-page.summary.no-liquidated')} ${yearSelected}`,
        redirectParam: {
          paramId: 'collectionStatus',
          value: yearSelected
        },
        icon: {
          name: 'money-check-dollar-pen' as IconName,
          color: this.configurationStyles.PCOL!
        }

      }
    }

    const attendanceFeeTitles = {
      liquidationStatus: {
        [LiquidationTypes.LIQUIDATED]: this.translateService.instant('home-page.summary.liquidation-paid'),
        [LiquidationTypes.NOT_LIQUIDATED]: this.translateService.instant('home-page.summary.liquidation-pending'),
      },
      collectionStatus: {
        [CollectionTypes.CLOSED]: this.translateService.instant('home-page.summary.collection-billed'),
        [CollectionTypes.NOT_CLOSED]: this.translateService.instant('home-page.summary.collection-pending'),
      }

    }

    const states = {
      byLiquidationStatus,
      byCollectionStatus
    }

    //Each attribute of the states is traversed, and with its 'key' the values are mapped by
    //taking them from the constants declared above (key as keyof typeof <value> is by typescript).
    Object.keys(states).forEach((key: string) => {
      const descriptionLabel = feeInformation[key as keyof typeof feeInformation].title;
      const icon = feeInformation[key as keyof typeof feeInformation].icon;
      const redirectParam = feeInformation[key as keyof typeof feeInformation].redirectParam;
      const totalTitle = feeInformation[key as keyof typeof feeInformation].totalTitle;

      const metrics: SummaryMetric[] = states[key as keyof typeof states]
        .map((attendanceFeeSummary: AttendanceFeeSummary) => {

          let description = '';
          let paramId = '';
          if (key === 'byLiquidationStatus') {
            description = attendanceFeeTitles.liquidationStatus[attendanceFeeSummary.liquidationStatusId as keyof typeof attendanceFeeTitles.liquidationStatus];
            paramId = `liquidationStatus_${attendanceFeeSummary.liquidationStatusId}`;

          } else if (key === 'byCollectionStatus') {
            description = attendanceFeeTitles.collectionStatus[attendanceFeeSummary.collectionStatusId as keyof typeof attendanceFeeTitles.collectionStatus];
            paramId = `collectionStatus_${attendanceFeeSummary.collectionStatusId}`;
          }

          return {
            description: description,
            amount: attendanceFeeSummary.attendanceAmount,
            redirectParam: {
              paramId: paramId,
              value: yearSelected
            }
          }
        })

      const summaryFee: SummaryFee = {
        totalTitle: totalTitle,
        descriptionLabel: descriptionLabel,
        metrics: metrics,
        redirectParam: redirectParam,
        icon: icon,
      }

      dataFee.push(summaryFee);

    })

    return dataFee;
  }

  private prepareAnnualFeePercentageData(totalBilledAmount: number, percentage: number, yearSelected: string) {
    let dataChart: DataChart = {
      chartConfigurations: [],
      title: `${this.translateService.instant('home-page.summary.billed')} ${yearSelected}`
    }

    const percentageWithoutDecimals = Number(percentage.toFixed(0));

    let dataPercentage = {
      billedPercentage: percentageWithoutDecimals,
      dataChart: dataChart,
      redirectParam: {
        paramId: 'billingStatus',
        value: yearSelected
      }
    }

    //Calculates the amount for billedAmount to be rendered correctly in doghnut-chart
    const billedAmount = totalBilledAmount * (percentageWithoutDecimals / 100);
    const unBilledAmount = totalBilledAmount - billedAmount;

    const chartConfigurationBilled: ChartConfiguration = {
      value: Number(billedAmount.toFixed(1)),
      backgroundColor: this.configurationStyles.SCOL
    }
    const chartConfigurationUnBilled: ChartConfiguration = {
      value: Number(unBilledAmount.toFixed(1)),
      backgroundColor: "#DADCDF"
    }

    dataChart.chartConfigurations = [chartConfigurationBilled, chartConfigurationUnBilled];

    return dataPercentage;
  }

}
